class MediaCarousel extends HTMLElement {
  connectedCallback() {
    if (!this.dataset.carousel) return;

    this.element = this.querySelector('[carousel-list]');
    this.elements = this.querySelectorAll('[carousel-list] > li');
    this.prevButton = this.querySelector('[previous-image]');
    this.nextButton = this.querySelector('[next-image]');
    this.currentIndex = 0;
    this.peekTimeout = null;

    this.observer = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          const targetElement = entry.target;

          if (entry.isIntersecting) {
            this.currentIndex = entry.target.index;
            document.dispatchEvent(new CustomEvent('custom:carousel:index', { detail: entry.target.index }));

            targetElement.setAttribute('aria-hidden', 'false');

            // Updates accessibility for all other slides
            this.elements.forEach((element, index) => {
              if (element !== targetElement) {
                element.setAttribute('aria-hidden', 'true');
                element.querySelectorAll('a, button, [tabindex]').forEach((focusable) => {
                  focusable.setAttribute('tabindex', '-1');
                });
              } else {
                element.querySelectorAll('a, button, [tabindex]').forEach((focusable) => {
                  focusable.removeAttribute('tabindex');
                });
              }
            });
          }
        });
      },
      {
        threshold: 0.51,
      },
    );

    this.elements.forEach((element, index) => {
      element.index = index;
      this.observer.observe(element);
    });

    if (this.dataset.desktopTouch) {
      this.initDesktopTouch();
    }

    // if this is the first instance on the page
    const firstInstance = document.querySelector('media-carousel');
    let peakCollectionVisits = parseInt(this.getCookie('peak_collection_visits'));

    if (isNaN(peakCollectionVisits)) {
      peakCollectionVisits = 0;
    }

    const maxPeakVisits = 3;
    const notReachedMaxPeakVisits = peakCollectionVisits != maxPeakVisits;

    if (
      notReachedMaxPeakVisits &&
      this === firstInstance &&
      window.innerWidth <= 767 &&
      !this.closest('[no-product-peek]')
    ) {
      this.setCookie('peak_collection_visits', peakCollectionVisits + 1, 30);

      this.element.querySelectorAll('img[loading]').forEach((el) => el.removeAttribute('loading'));
      this.peekTimeout = setTimeout(() => {
        this.initPeek();
      }, 2000);
    }

    this.addEventListener('touchstart', (e) => {
      this.element.querySelectorAll('img[loading]').forEach((el) => el.removeAttribute('loading'));
      if (this.peekTimeout) {
        clearTimeout(this.peekTimeout);
      }
    });

    this.addEventListener('mouseenter', (e) => {
      this.element.classList.remove('scroll-smooth');
      this.element.querySelectorAll('img[loading]').forEach((el) => el.removeAttribute('loading'));
      let multiplier = this.dataset.hoverOnly ? 1 : 2;
      this.element.scrollLeft = this.element.getBoundingClientRect().width * multiplier;
      requestAnimationFrame(() => {
        this.element.classList.add('scroll-smooth');
        this.updateButtons();
      });
    });

    this.addEventListener('mouseleave', (e) => {
      this.element.classList.remove('scroll-smooth');
      this.element.scrollLeft = 0;
      requestAnimationFrame(() => {
        this.element.classList.add('scroll-smooth');
        this.updateButtons();
      });
    });

    this.prevButton?.addEventListener('click', (e) => {
      e.preventDefault();
      this.element.scrollLeft -= this.element.getBoundingClientRect().width;
      this.updateButtons();
    });
    this.nextButton?.addEventListener('click', (e) => {
      e.preventDefault();
      this.element.scrollLeft += this.element.getBoundingClientRect().width;
      this.updateButtons();
    });
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  initPeek() {
    let leftOffset = this.element.getBoundingClientRect().width * 0.35;
    this.element.classList.add('snap-none');
    requestAnimationFrame(() => {
      this.elements[1].style.display = 'none';
      this.elements.forEach((element) => {
        element.classList.add('pointer-events-none', 'select-none', '-translate-x-2/4', 'duration-1000');
      });
      waitForTransitionEnd(this.elements[0]).then(() => {
        this.element.scrollLeft = 0;
        requestAnimationFrame(() => {
          this.elements.forEach((element) => element.classList.remove('-translate-x-2/4'));
          waitForTransitionEnd(this.elements[0]).then(() => {
            this.element.classList.remove('snap-none');
            this.elements.forEach((element) =>
              element.classList.remove('pointer-events-none', 'select-none', 'duration-1000'),
            );
            this.elements[1].style.display = '';
          });
        });
      });
    });
  }

  initSlowScroll(leftOffset) {
    const start = this.element.scrollLeft;
    const startTime = 'now' in window.performance ? performance.now() : new Date().getTime();
    const duration = 2000;
    const easings = {
      linear(t) {
        return t;
      },
      easeOutQuad(t) {
        return t * (2 - t);
      },
    };

    function scroll() {
      const now = 'now' in window.performance ? performance.now() : new Date().getTime();
      const time = Math.min(1, (now - startTime) / duration);
      const timeFunction = easings['easeOutQuad'](time);
      this.element.scroll(Math.ceil(timeFunction * (leftOffset - start) + start), 0);

      if (this.element.scrollLeft === leftOffset) {
        return;
      }

      requestAnimationFrame(scroll.bind(this));
    }

    scroll.bind(this)();
  }

  updateButtons() {
    setTimeout(() => {
      if (this.element.scrollLeft === 0) {
        this.prevButton?.classList.add('opacity-40');
      } else {
        this.prevButton?.classList.remove('opacity-40');
      }
      let totalWidth = Math.round(
        this.element.getBoundingClientRect().width * this.element.querySelectorAll('li').length,
      );
      let currentWidth = Math.round(this.element.getBoundingClientRect().width + this.element.scrollLeft);

      if (totalWidth === currentWidth) {
        this.nextButton?.classList.add('opacity-40');
      } else {
        this.nextButton?.classList.remove('opacity-40');
      }
    }, 400);
  }

  initDesktopTouch() {
    if (window.innerWidth <= 767) return;
    this.elements.forEach((element) => element.classList.add('pointer-events-none', 'select-none'));

    let isDrag = false;
    let timeout;
    const dragStart = (ev) => {
      console.log('dragStart');
      isDrag = true;
      this.element.classList.add('snap-none');
      if (timeout) clearTimeout(timeout);
    };
    const dragEnd = () => {
      console.log('dragEnd');
      if (!isDrag) return;
      isDrag = false;
      this.elements[this.currentIndex].scrollIntoView({ behavior: 'smooth' });
      timeout = setTimeout(() => {
        this.element.classList.remove('snap-none');
      }, 700);
    };
    const drag = (event) => {
      if (!isDrag) return;
      this.element.scrollLeft -= event.movementX * 1.6;
    };

    this.element.addEventListener('pointerdown', dragStart);
    this.element.addEventListener('pointerup', dragEnd);
    this.element.addEventListener('pointerleave', dragEnd);
    this.element.addEventListener('pointermove', drag);
  }

  getCookie(name) {
    const cookieArray = document.cookie.split(';');
    for (let i = 0; i < cookieArray.length; i++) {
      const cookiePair = cookieArray[i].split('=');
      if (name === cookiePair[0].trim()) {
        return decodeURIComponent(cookiePair[1]);
      }
    }
    return null;
  }

  setCookie(name, value, days) {
    let expires = '';
    if (days) {
      const date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      expires = '; expires=' + date.toUTCString();
    }
    document.cookie = `${name}=${value || ''}${expires}; path=/`;
  }
}

customElements.define('media-carousel', MediaCarousel);
